import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketSavedView, TicketSavedViewScope } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-saved-views-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './saved-views-list.component.html'
})
export class TicketsSavedViewsListComponent implements OnInit, OnDestroy {
  items: TicketSavedView[] = [];
  filtered: TicketSavedView[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroScope: TicketSavedViewScope | '' = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getSavedViews().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data.sort((a, b) => a.name.localeCompare(b.name));
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar vistas guardadas';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.items.filter((v) => {
      if (term && !v.name.toLowerCase().includes(term)) return false;
      if (this.filtroScope && v.scope !== this.filtroScope) return false;
      return true;
    });
  }

  scopeBadgeClass(scope: TicketSavedViewScope): string {
    switch (scope) {
      case 'PUBLIC':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'TEAM':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  }

  scopeLabel(scope: TicketSavedViewScope): string {
    switch (scope) {
      case 'PUBLIC':
        return 'Pública';
      case 'TEAM':
        return 'Equipo';
      default:
        return 'Privada';
    }
  }

  async eliminar(item: TicketSavedView): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar vista',
      message: `¿Eliminar la vista guardada "${item.name}"?`
    });
    if (!ok) return;
    this.configApi.deleteSavedView(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.deleted('Vista guardada', `Se eliminó "${item.name}" correctamente.`);
        this.cargar();
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo eliminar la vista'
        });
      }
    });
  }

  volverATickets(): void {
    this.router.navigate(['/tickets']);
  }
}
